<template>
  <component
    :is="interativo ? 'button' : 'div'"
    class="fish-slot"
    :class="[
      `fish-slot--${raridade}`,
      {
        'fish-slot--vazio': !item,
        'fish-slot--aceso': aceso,
        'fish-slot--selecionado': selecionado,
        'fish-slot--bloqueado': !!requisito,
        'fish-slot--arrastando': arrastando,
      },
    ]"
    :type="interativo ? 'button' : null"
    :aria-label="rotuloAcessivel"
    :aria-pressed="interativo && selecionado ? 'true' : null"
    @click="interativo && $emit('selecionar', item)"
    @dblclick="interativo && $emit('equipar', item)"
    @mouseenter="abrirDica"
    @mouseleave="fecharDica"
    @focus="abrirDica"
    @blur="fecharDica"
  >
    <!-- O esboço da peça: o encaixe VAZIO mostra o desenho do que cabe nele, não o nome (D4). -->
    <FishIcon v-if="!item && esboco" :nome="esboco" tamanho="grande" class="fish-slot__esboco" />

    <template v-if="item">
      <FishIcon v-if="item.imagem" :src="item.imagem" :alt="item.nome" tamanho="grande" :silhueta="silhueta" />
      <!--
        Sem arte, o encaixe diz o que a peça É — a ferramenta, a armadura, o acessório — em vez de
        um quadrado vazio. `FishIcon` com `src` nulo e sem `nome` renderiza NADA, e sem erro em
        lugar nenhum: foi assim que dez itens do Posto de Expedição viraram buracos na grade
        (visto na tela em 09/09/2026). Reserva, e não substituto: quando a arte chegar, ela ganha.
      -->
      <FishIcon
        v-else
        :nome="item.icone || esboco || 'box'"
        :alt="item.nome"
        tamanho="grande"
        class="fish-slot__sem-arte"
      />

      <!--
        A quantidade dos empilháveis. `player_items` já guarda `quantity`, ou seja **item já
        empilha no banco** — quem apresentava em lista era só o cliente (D17).

        `multiplicador` troca o REGISTRO do número: no inventário ele é o que você tem ("12"), na
        loja é o que vem no pacote ("×12"). Sem esse sinal, duas iscas de arte idêntica e preços
        diferentes ficam indistinguíveis na grade — que é o que a loja premium fazia.
      -->
      <span v-if="quantidade > 1" class="fish-slot__quantidade t-numero">{{ rotuloDaQuantidade }}</span>
    </template>

    <!--
      O requisito mora no PRÓPRIO encaixe (D16, 5.4), em vez de um card inteiro desabilitado
      ocupando espaço na grade. É a mesma economia que faz o paredão da loja virar grade.
    -->
    <!--
      A MARCA — a quem esta peça serve, no topo do encaixe (09/09/2026).

      Vizinha do `requisito` e o oposto dele. `requisito` diz **bloqueado**: acinzenta a arte e o
      leitor de tela anuncia "bloqueado: Nv. 12". A marca diz apenas *de quem é* — a Lâmina Afiada
      é do mercenário e a Tesoura de Poda é do botânico, as duas à venda, as duas boas. Enfiar as
      duas coisas na mesma tarja acinzentaria metade do Posto de Expedição como se estivesse fora
      de alcance.

      Em cima, e não embaixo, para as duas poderem conviver no mesmo encaixe sem se cobrir.
    -->
    <span v-if="marca" class="fish-slot__marca">{{ marca }}</span>

    <span v-if="requisito" class="fish-slot__requisito">{{ requisito }}</span>

    <span v-if="equipado" class="fish-slot__equipado" aria-hidden="true">
      <FishIcon nome="check" tamanho="miudo" />
    </span>

    <!--
      A DICA — o bilhete que aparece ao passar o mouse (ou ao focar pelo teclado).

      A grade mostra só arte e raridade de propósito (D16); a dica é o meio-termo entre
      "reconhecer de relance" e "abrir o detalhe": nome, e o que o dono da grade quiser dizer em
      poucas linhas — a chance de cada prêmio de um baú, o efeito de um tônico. O jogador pediu
      isso de volta em 07/09/2026: o `ItemCard` antigo tinha o tooltip e a grade o perdeu.

      **Vai para o `<body>`, e não fica dentro do encaixe.** A grade da loja rola dentro de um
      `overflow-y: auto`, e overflow **recorta** o que passa da borda por mais alto que seja o
      `z-index` — o bilhete da última linha saía cortado, e o das outras aparecia atrás dos
      encaixes vizinhos (09/09/2026). Teleportado e em `position: fixed` ele não tem container que
      o corte nem irmão que o cubra; o preço é calcular a posição na mão, aqui embaixo.
    -->
    <Teleport to="body">
      <!--
        A moldura é a MESMA de `FishFrame` (`frame-panel.png`, 9-slice): o bilhete é um painel de
        papel pequeno, e não uma caixinha de CSS com borda lisa. Sobre papel se escreve com tinta
        (`--tinta*`), nunca com a família `--texto*` — a lição do menu ilegível de 03/09.
      -->
      <div v-if="dicaAberta" ref="dica" class="fish-slot__dica" role="tooltip" :style="estiloDaDica">
        <p class="fish-slot__dica-titulo">{{ dica.titulo }}</p>

        <p v-if="dica.raridade" class="fish-slot__dica-raridade" :class="`fish-slot__dica-raridade--${dica.raridade}`">
          {{ dica.raridadeRotulo || dica.raridade }}
        </p>

        <p v-if="dica.legenda" class="fish-slot__dica-legenda">{{ dica.legenda }}</p>

        <p v-for="(frase, i) in dica.texto || []" :key="`t${i}`" class="fish-slot__dica-frase">
          {{ frase }}
        </p>

        <!-- Rótulo à esquerda, número à direita: é a leitura de ficha, e o `t-numero` alinha os
             dígitos na coluna em vez de deixá-los dançando. -->
        <dl v-if="(dica.atributos || []).length" class="fish-slot__dica-atributos">
          <!-- O `div` entre `dl` e `dt`/`dd` é HTML5 válido e existe para agrupar o par: sem ele
               a chave do `v-for` teria de ir num `<template>`, que o lint recusa. -->
          <div v-for="a in dica.atributos" :key="a.rotulo" class="fish-slot__dica-atributo">
            <dt>{{ a.rotulo }}</dt>
            <dd class="t-numero">{{ a.valor }}</dd>
          </div>
        </dl>

        <!--
          A tabela de prêmios do baú. Duas colunas: a chance numa largura fixa, para as
          porcentagens ficarem alinhadas umas sob as outras, e o prêmio ocupando o resto. Em
          linhas soltas de texto ("48% · 250–400× Camarão") o olho não compara as chances — que é
          exatamente para o que essa tabela existe.
        -->
        <div v-if="(dica.premios || []).length" class="fish-slot__dica-premios">
          <p v-if="dica.premiosTitulo" class="fish-slot__dica-secao">{{ dica.premiosTitulo }}</p>
          <p v-for="(p, i) in dica.premios" :key="`p${i}`" class="fish-slot__dica-premio">
            <span class="t-numero fish-slot__dica-chance">{{ p.chance }}%</span>
            <span class="fish-slot__dica-premio-nome">{{ p.nome }}</span>
          </p>
        </div>
      </div>
    </Teleport>
  </component>
</template>

<script>
import FishIcon from './FishIcon.vue';

/**
 * 2.5 — `FishSlot`: o encaixe, com raridade, quantidade e requisito (D16, D17).
 *
 * ## O que este componente já pode fazer, e não custa backend
 *
 * A descoberta que muda o custo do D17: **o modelo de equipamento já é por slot, no banco.**
 * `player_gear` tem `rodId`, `boatId`, `houseId` e uma **coluna gerada `equipped_slot`** com índice
 * único em `(player_id, equipped_slot)` — o MySQL garante "uma vara equipada por jogador" na própria
 * tabela. E `player_items` já guarda `quantity`.
 *
 * **A UI de encaixes não precisa de mudança de backend para equipar** (6.5). Quem apresentava em
 * lista era só o cliente.
 *
 * ## O aro aceso
 *
 * `aceso` é o estado "este encaixe aceita o que você está segurando" (D17, 6.4). É ele que faz o
 * caminho de dois toques funcionar: **tocar no item → os encaixes válidos acendem → tocar no
 * encaixe**. Sem o aceso, dois toques viram adivinhação.
 *
 * ## Por que `<button>` quando é interativo
 *
 * Não é preciosismo: `<div @click>` não recebe foco, não responde a Enter nem a Espaço, e não é
 * anunciado como acionável. Com 215 `:hover` e seis `:focus` no cliente (UI-26, UI-29), a
 * navegação por teclado no inventário seria impossível — e o D17 é explícito em que **arrastar
 * nunca pode ser o único caminho** (D11).
 */
export default {
  name: 'FishSlot',
  components: { FishIcon },
  props: {
    /**
     * `{ id, nome, imagem, raridade?, icone? }`. `null` deixa o encaixe vazio.
     *
     * `icone` é o desenho de reserva para quando `imagem` falta — a peça existe, só não tem arte.
     */
    item: { type: Object, default: null },
    quantidade: { type: Number, default: 1 },
    /** O número é tamanho de PACOTE (`×12`), não estoque possuído (`12`). */
    multiplicador: { type: Boolean, default: false },
    /** O esboço do encaixe vazio: `slot-vara`, `slot-isca`, `slot-anzol`. */
    esboco: { type: String, default: '' },
    /** O que falta, escrito no encaixe. Ex.: "Nv. 12". Acinzenta a peça. */
    requisito: { type: String, default: '' },
    /**
     * De quem é a peça, escrito no topo do encaixe. Ex.: "Botânico".
     *
     * **Não** é requisito: não acinzenta nada e não anuncia bloqueio. Serve ao caso em que a peça
     * está à venda e é boa, mas só uma parte do elenco a usa — as cinco ferramentas de guilda do
     * Posto de Expedição, que na grade eram cinco desenhos parecidos e nenhum dizia de quem era.
     */
    marca: { type: String, default: '' },
    equipado: { type: Boolean, default: false },
    selecionado: { type: Boolean, default: false },
    /** Aceita o que está sendo arrastado ou foi tocado (D17). */
    aceso: { type: Boolean, default: false },
    arrastando: { type: Boolean, default: false },
    silhueta: { type: Boolean, default: false },
    interativo: { type: Boolean, default: true },
    /**
     * O bilhete do hover/foco. Sem `titulo`, não há bilhete.
     *
     * É um objeto e não uma lista de frases (era, até 09/09/2026) porque cada parte é desenhada
     * de um jeito: o título em fonte de display, a raridade na cor dela, os atributos em duas
     * colunas, as chances alinhadas em coluna própria. Com frases prontas o componente não tem
     * como alinhar nada — e alinhar as porcentagens é o ponto da tabela do baú.
     *
     * ```
     * {
     *   titulo: 'Baú de Poseidon',
     *   raridade: 'lendario', raridadeRotulo: 'Lendário',
     *   legenda: 'Pacote com 5 · 120 cada',
     *   texto: ['O que o catálogo diz.'],
     *   atributos: [{ rotulo: 'Poder', valor: 6 }],
     *   premiosTitulo: 'Possíveis Recompensas',
     *   premios: [{ chance: 48, nome: '250–400× Camarão' }],
     * }
     * ```
     */
    dica: { type: Object, default: () => ({}) },
  },
  emits: ['selecionar', 'equipar'],
  data() {
    return { dicaAberta: false, dicaAcima: false, estiloDaDica: null };
  },
  computed: {
    /** `12` no inventário, `×12` na loja — ver `multiplicador`. */
    rotuloDaQuantidade() {
      return this.multiplicador ? `×${this.quantidade}` : String(this.quantidade);
    },
    raridade() {
      return this.item?.raridade || this.item?.rarity || 'comum';
    },
    /**
     * O anúncio do encaixe, em uma frase.
     *
     * Um leitor de tela lendo "botão, botão, botão" numa grade de trinta encaixes é o mesmo que
     * silêncio. A frase junta tudo o que o olho vê de relance: o que é, quantos, e o estado.
     */
    rotuloAcessivel() {
      if (!this.item) return this.esboco ? 'Encaixe vazio' : 'Vazio';
      const partes = [this.item.nome];
      if (this.quantidade > 1) {
        partes.push(
          this.multiplicador ? `pacote com ${this.quantidade}` : `${this.quantidade} unidades`,
        );
      }
      if (this.equipado) partes.push('equipado');
      // "para Botânico", e não "bloqueado": a marca não impede nada. Ver a prop `marca`.
      if (this.marca) partes.push(`para ${this.marca}`);
      if (this.requisito) partes.push(`bloqueado: ${this.requisito}`);
      return partes.join(', ');
    },
  },
  beforeUnmount() {
    this.fecharDica();
  },
  methods: {
    /**
     * Abre o bilhete e o ancora no encaixe.
     *
     * Abaixo por padrão, **acima** quando não cabe embaixo — a última linha da grade é o caso
     * comum, e é lá que o bilhete aparecia cortado. A conta é sobre a janela, e não sobre o
     * container que rola, porque em `position: fixed` é a janela que limita.
     */
    abrirDica() {
      if (!this.item || !this.dica?.titulo) return;

      /*
       * MEDE, e só então mostra (28/09/2026).
       *
       * A versão anterior ESTIMAVA a altura (40px + 19 por linha) para não piscar. A estimativa
       * ignorava o filete e o respiro entre blocos e a quebra de linha de "Qualquer integrante da
       * squad" — o bilhete das Botas Leves (seis blocos) media o dobro do estimado. Num celular de
       * 360px, com o encaixe no meio da tela, a conta dizia "cabe embaixo", o bilhete real não
       * cabia, e ele saía espremido contra a borda e atrás do rodapé.
       *
       * Renderiza invisível, mede no `nextTick` e posiciona — tudo antes do próximo quadro, então
       * não pisca: o `nextTick` é microtarefa, roda antes de o navegador pintar.
       */
      this.estiloDaDica = { left: '0px', top: '0px', visibility: 'hidden' };
      this.dicaAberta = true;
      this.$nextTick(this.posicionarDica);

      // Rolou a grade: o encaixe saiu de baixo do mouse e o bilhete ficaria pendurado no lugar
      // antigo. `capture` porque quem rola é o container, não a janela.
      window.addEventListener('scroll', this.fecharDica, true);
    },
    /**
     * Embaixo do encaixe quando cabe; em cima quando só lá cabe; e, quando não cabe inteiro em
     * nenhum dos dois lados (telefone, encaixe no meio da tela), encostado na borda da janela que
     * tem mais espaço — cobrindo o encaixe, mas inteiro. Na horizontal, preso à janela pela
     * largura REAL do bilhete.
     */
    posicionarDica() {
      const el = this.$refs.dica;
      if (!this.dicaAberta || !el || !this.$el?.getBoundingClientRect) return;

      const r = this.$el.getBoundingClientRect();
      const FOLGA = 6;
      const janelaL = window.innerWidth;
      const janelaA = window.innerHeight;
      const { offsetWidth: largura, offsetHeight: altura } = el;

      const embaixo = janelaA - r.bottom - FOLGA;
      const emCima = r.top - FOLGA;
      let topo;
      if (altura <= embaixo) topo = r.bottom + FOLGA;
      else if (altura <= emCima) topo = r.top - FOLGA - altura;
      else topo = embaixo >= emCima ? janelaA - altura - FOLGA : FOLGA;
      topo = Math.max(FOLGA, Math.min(topo, janelaA - altura - FOLGA));
      this.dicaAcima = topo < r.top;

      const metade = largura / 2 + FOLGA;
      const meio = Math.min(janelaL - metade, Math.max(metade, r.left + r.width / 2));

      this.estiloDaDica = { left: `${meio}px`, top: `${topo}px` };
    },
    fecharDica() {
      this.dicaAberta = false;
      window.removeEventListener('scroll', this.fecharDica, true);
    },
  },
};
</script>

<style scoped>
.fish-slot {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  aspect-ratio: 1;
  /* O mínimo do D11. Um encaixe de 40px numa grade de oito colunas é um alvo que erra. */
  min-width: var(--alvo-minimo);
  min-height: var(--alvo-minimo);
  padding: 0;

  /* A mesma placa do botão, com a luz e a sombra invertidas no PNG: o encaixe **afunda** onde o
   * botão sobe. Por isso a fatia é a mesma — é literalmente a mesma arte, recolorida ao contrário. */
  border-style: solid;
  border-color: transparent;
  border-width: var(--placa-borda);
  border-image-source: url('/ui/encaixe.png');
  border-image-slice: var(--placa-fatia);
  border-image-width: var(--placa-borda);
  border-image-repeat: repeat;
  background: var(--fundo-encaixe);
  background-clip: padding-box;
  image-rendering: pixelated;

  transition: transform var(--t-rapido) var(--curva-quique),
    box-shadow var(--t-rapido) var(--curva-suave);
}

.fish-slot:not(.fish-slot--vazio) {
  border-image-source: url('/ui/encaixe-cheio.png');
}

/**
 * A raridade é a COR DO ARO, não o fundo.
 *
 * Fundo colorido brigaria com a arte do item — e a arte é o que precisa ser lida primeiro. O aro
 * diz a raridade na periferia da visão, que é onde a informação de raridade pertence: ela é útil ao
 * varrer a grade, não ao olhar um item.
 */
.fish-slot--comum {
  --aro: var(--raridade-comum);
}
.fish-slot--incomum {
  --aro: var(--raridade-incomum);
}
.fish-slot--rara,
.fish-slot--raro {
  --aro: var(--raridade-rara);
}
.fish-slot--epica,
.fish-slot--epico {
  --aro: var(--raridade-epica);
}
.fish-slot--lendaria,
.fish-slot--lendario {
  --aro: var(--raridade-lendaria);
}
.fish-slot--mitica,
.fish-slot--mitico {
  --aro: var(--raridade-mitica);
}

.fish-slot:not(.fish-slot--vazio)::after {
  content: '';
  position: absolute;
  inset: -2px;
  border: 2px solid var(--aro, var(--raridade-comum));
  pointer-events: none;
}

@media (hover: hover) {
  .fish-slot:not(.fish-slot--vazio):hover {
    transform: translateY(-2px);
  }
}

/* O toque responde NO DEDO (autor, 03/09: faltava animação de clique): o encaixe afunda um tico
 * e volta pelo quique da transição — jogo responde, aplicativo só registra. */
.fish-slot:not(.fish-slot--vazio):active {
  transform: translateY(1px) scale(0.94);
}

.fish-slot--selecionado {
  box-shadow: 0 0 0 3px var(--ouro-claro);
}

/* O aceso: "solte aqui" / "toque aqui". Respira devagar, como tudo neste jogo (D0). */
.fish-slot--aceso {
  animation: repouso-brilhar 2.4s var(--curva-agua) infinite;
  box-shadow: 0 0 0 2px var(--ouro);
}

/* O que está sendo arrastado fica no lugar, apagado: é o "fantasma" que diz de onde o item saiu.
 * Sumir com ele faria a grade re-fluir no meio do arrasto, e o alvo mudaria de lugar sob o dedo. */
.fish-slot--arrastando {
  opacity: 0.35;
}

.fish-slot--bloqueado {
  filter: grayscale(0.7);
}

.fish-slot__esboco {
  opacity: 0.85; /* o PNG já vem achatado com alfa 34% — aqui é só um ajuste fino */
}

/* A arte do item preenche o encaixe em vez de flutuar nele: 48px fixos deixavam o produto perdido
 * num encaixe de ~70 (pedido do autor, 02/09: "aumentar as imagens dos produtos"). Em porcentagem,
 * a arte cresce junto com o encaixe em qualquer grade. O esboço do vazio acompanha. */
.fish-slot .fish-icon--arte,
.fish-slot__esboco {
  width: 78%;
  height: 78%;
}

.fish-slot__quantidade {
  position: absolute;
  right: 2px;
  bottom: 0;
  font-size: var(--f-1);
  color: var(--texto);
  text-shadow: 1px 1px 0 var(--agua-abissal), -1px 1px 0 var(--agua-abissal),
    1px -1px 0 var(--agua-abissal), -1px -1px 0 var(--agua-abissal);
}

/*
 * A dica: um papel pequeno ancorado no encaixe.
 *
 * Ela vive no `<body>` (o `Teleport` do template), e não dentro do encaixe — por isso
 * `position: fixed` e o `left`/`top` vindo do `style`, calculados em `abrirDica`.
 *
 * ## Por que sair de dentro do encaixe
 *
 * Enquanto o bilhete era filho do encaixe, ele apanhava dos dois lados: os encaixes da linha de
 * baixo, desenhados depois, pintavam **por cima** dele (todos são `position: relative` sem
 * z-index, então quem vem depois no DOM ganha); e o `overflow-y: auto` da grade **recortava** o
 * que passasse da borda — e overflow recorta independentemente de z-index, então nenhum número
 * aqui resolveria a última linha. Fora do container não há o que corte nem irmão que cubra.
 *
 * `pointer-events: none` para o bilhete não roubar o mouse do encaixe que o abriu — sem isso ele
 * pisca, porque aparecer sob o cursor dispara o `mouseleave` do encaixe.
 */
.fish-slot__dica {
  position: fixed;
  transform: translateX(-50%);
  min-width: 168px;
  /* Largo o bastante para "Vara do Pescador Noturno" caber numa linha — o nome mais longo do
   * catálogo de prêmios. Abaixo disso a tabela do baú quebra e as chances deixam de se alinhar. */
  /* E nunca mais largo que a janela menos a folga: em 360px, 300 de bilhete ancorado num encaixe
   * da ponta não tinha para onde ir. */
  max-width: min(300px, calc(100vw - 12px));
  /* A MESMA moldura de `FishFrame`: 9-slice de `frame-panel.png`, papel recortado. Uma borda
   * lisa de 2px ao lado dos painéis entalhados do jogo lê como caixa de navegador. */
  border: var(--moldura-borda) solid transparent;
  border-image: url('/ui/frame-panel.png') var(--moldura-fatia) / var(--moldura-borda) repeat;
  background: var(--papel-luz);
  background-clip: padding-box;
  image-rendering: pixelated;
  padding: var(--e-2) var(--e-3);
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  /* A sombra projetada afasta o papel do fundo. Dura, sem desfoque: o jogo é pixel art, e o
   * `drop-shadow` acompanha o recorte da moldura em vez de desenhar um retângulo atrás dela. */
  filter: drop-shadow(0 3px 0 rgba(6, 14, 22, 0.45));
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  line-height: 1.4;
  text-align: left;
  white-space: normal;
  color: var(--tinta);
  pointer-events: none;
  /* Acima de tudo: modais do jogo ficam na casa dos 100, e a dica é sempre o que está sob o
   * cursor — se ela aparece, é ela que se lê. */
  z-index: 9000;
}

/* O empilhamento é por `gap`, e não por margem: assim nenhuma regra precisa vencer outra, e
 * separar dois blocos é só acrescentar o filete de cima. */
.fish-slot__dica > * {
  margin: 0;
}

/* O título em fonte de display, como `.fish-frame__titulo` — o bilhete pertence à mesma família
 * dos painéis do jogo, e não a uma caixinha à parte. */
.fish-slot__dica-titulo {
  font-family: var(--fonte-display);
  font-size: var(--f-3);
  line-height: var(--lh-curto);
  color: var(--tinta);
}

.fish-slot__dica-raridade {
  font-size: var(--f-1);
  line-height: 1.2;
}

.fish-slot__dica-legenda,
.fish-slot__dica-frase,
.fish-slot__dica-atributos,
.fish-slot__dica-premios {
  padding-top: var(--e-1);
  border-top: 2px solid var(--papel-sombra);
}

/* Duas frases seguidas são um parágrafo só; o filete separa BLOCOS, não linhas. */
.fish-slot__dica-frase + .fish-slot__dica-frase {
  padding-top: 0;
  border-top: 0;
}

.fish-slot__dica-legenda,
.fish-slot__dica-frase {
  color: var(--tinta-fraca);
  font-size: var(--f-1);
}

/* Rótulo à esquerda, número à direita — a leitura de ficha. */
.fish-slot__dica-atributo {
  display: flex;
  justify-content: space-between;
  gap: var(--e-3);
  font-size: var(--f-1);
}

.fish-slot__dica-atributo dt {
  color: var(--tinta-fraca);
}

.fish-slot__dica-atributo dd {
  margin: 0;
  color: var(--tinta);
}

.fish-slot__dica-secao {
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  margin-bottom: 2px !important;
}

/*
 * A linha do prêmio, em duas colunas.
 *
 * A chance tem largura FIXA para as porcentagens ficarem alinhadas umas sob as outras — é o que
 * deixa comparar 48% com 12% de relance. Em linha corrida ("48% · 250–400× Camarão") os números
 * começam em posições diferentes e a comparação exige ler tudo.
 */
.fish-slot__dica-premio {
  display: grid;
  grid-template-columns: 3.2em 1fr;
  gap: var(--e-2);
  font-size: var(--f-1);
  align-items: baseline;
}

.fish-slot__dica-chance {
  text-align: right;
  color: var(--tinta);
}

.fish-slot__dica-premio-nome {
  color: var(--tinta-fraca);
}

/* A raridade fala pela cor, como o aro do encaixe. Os tokens são os mesmos. */
.fish-slot__dica-raridade--comum { color: var(--raridade-comum); }
.fish-slot__dica-raridade--incomum { color: var(--raridade-incomum); }
.fish-slot__dica-raridade--rara,
.fish-slot__dica-raridade--raro { color: var(--raridade-rara); }
.fish-slot__dica-raridade--epica,
.fish-slot__dica-raridade--epico { color: var(--raridade-epica); }
.fish-slot__dica-raridade--lendaria,
.fish-slot__dica-raridade--lendario { color: var(--raridade-lendaria); }
.fish-slot__dica-raridade--mitica,
.fish-slot__dica-raridade--mitico { color: var(--raridade-mitica); }

/*
 * A marca, no topo. Mesma tarja do requisito, e de propósito — é a mesma peça de UI, uma faixa
 * legível sobre a arte —, mas em `--texto` em vez do `--cor-aviso`: amarelo de aviso num rótulo
 * que não avisa nada leria como problema. A tarja é fundo ESCURO, então a cor vem da família
 * `--texto`; `--tinta` é para papel, e aqui sumiria. O nome longo corta com reticências: num
 * encaixe de ~70px "Paleontólogo" não cabe, e o que importa ali é *ter* marca — quem quer o nome
 * inteiro passa o mouse e lê o bilhete.
 */
.fish-slot__marca {
  position: absolute;
  inset: 0 0 auto 0;
  background: rgba(14, 27, 38, 0.85);
  color: var(--texto);
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  line-height: 1.4;
  padding: 1px 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fish-slot__requisito {
  position: absolute;
  inset: auto 0 0 0;
  background: rgba(14, 27, 38, 0.85);
  color: var(--cor-aviso);
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  line-height: 1.4;
  padding: 1px 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fish-slot__equipado {
  position: absolute;
  top: -4px;
  right: -4px;
  background: var(--cor-bom);
  border: 2px solid var(--agua-abissal);
  padding: 1px;
  line-height: 0;
}
</style>
